import { computed, effect, inject, Injectable } from "@angular/core";
import { WkDataTablePersistenceService } from "./persistence";
import { WkDataTableRegistryService } from "./registry";
import type {
    WkDataTableColumns,
    WkDataTableStateColumn,
    WkDataTableStateColumns,
    WkDataTableFieldName,
    WkDataTableColumnDefinition,
} from "@components/data-table/data-table.types";
import { WkDataTableSimpleColumn } from "@components/data-table/columns";
import { computedEqual } from "@shared/utils";

@Injectable()
export class WkDataTableStoreService {
    private readonly persistence = inject(WkDataTablePersistenceService);
    private readonly registry = inject(WkDataTableRegistryService);

    public readonly columns = computed(() => this.registry.list());
    public readonly sortedColumns = computed(() => this.columns().toSorted(this.sortColumns));
    public readonly visibleColumns = computed(() => this.sortedColumns().filter((column) => column.isVisible()));
    public readonly lockedColumns = computed(() => this.visibleColumns().filter((column) => column.definition.locked));

    public readonly hasLockedColumns = computed(() => this.lockedColumns().length > 0);

    public readonly state = computed(
        () => {
            const states: WkDataTableStateColumns = {};
            this.registry.list().forEach((column) => {
                const state = column.state();
                const fieldName = column.fullFieldName();
                states[fieldName] = state;
            });
            return states;
        },
        {
            equal: computedEqual,
        },
    );

    private persistGuard = false;
    private initializing = false;
    private firstRun = true;

    public loadState(persistenceId: string): void {
        this.persistGuard = true;
        this.persistence.setId(persistenceId);
        const loadedColumns = this.persistence.load();
        for (const column of this.registry.list()) {
            const state = loadedColumns?.[column.fullFieldName()];
            if (state) column.setState(state);
        }
        this.persistGuard = false;
    }

    public setColumns(columns: WkDataTableColumns): void {
        this.persistGuard = true;
        const loadedState = this.persistence.load();
        this.registry.initialize(columns, loadedState);
        this.persistGuard = false;
        this.initializing = true;
    }

    public setState(fieldName: WkDataTableFieldName, state: Partial<WkDataTableStateColumn>): void {
        this.get(fieldName)?.setState(state);
    }

    public setStates(state: WkDataTableStateColumns): void {
        this.persistGuard = true;
        this.columns().forEach((column) => {
            const fieldName = column.fullFieldName();
            const columnState = state[fieldName];
            this.setState(fieldName, columnState);
        });
        this.persistGuard = false;
    }

    public clearState(fieldName: WkDataTableFieldName): void {
        this.get(fieldName)?.clearState();
    }

    public clearAll(): void {
        this.persistGuard = true;
        this.registry.list().forEach((column) => column.clearState());
        this.persist();
        this.persistGuard = false;
    }

    private persist(): void {
        const selectionState = this.state();
        this.persistence.save(selectionState);
    }

    public get(fieldName: WkDataTableFieldName): WkDataTableSimpleColumn<any, any, WkDataTableColumnDefinition> | undefined {
        return this.registry.get(fieldName);
    }

    private sortColumns(a: WkDataTableSimpleColumn<any, any>, b: WkDataTableSimpleColumn<any, any>): number {
        if ((!a && b) || (a?.position() === undefined && b?.position() !== undefined)) return -1;
        if ((a && !b) || (a?.position() !== undefined && b?.position() === undefined)) return 1;
        if ((!a && !b) || (a?.position() === undefined && b?.position() === undefined)) return 0;
        return (a?.position() ?? 0) - (b?.position() ?? 0);
    }

    constructor() {
        effect(() => {
            this.state();
            if (this.persistGuard || !this.initializing) return;
            if (this.firstRun) {
                this.firstRun = false;
                return;
            }

            this.persist();
        });
    }
}
